import React, { useState, useEffect, useMemo } from "react";
import TaskTable from "./DataTable";
import images from "../../assets/images";
import AddTask from "./AddTask";
import { Dropdown } from "../../components/dropdown/Dropdown";
import { usePermissions } from "../../hooks/usePermission";
import {
  TASK__MODULE,
  TASK_MODULE_LIST_FIRE_SAFETY,
} from "../../constants/modules";
import { useTaskStore, usePremisesStore } from "../../store/store";

const Tasks: React.FC = () => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [selectedRegion, setSelectedRegion] = useState<string>("");
  const [selectedPremises, setSelectedPremises] = useState<string>("");
  const [selectedModule, setSelectedModule] = useState<string>("");

  const { canCreate } = usePermissions();
  const { tasks, tasksWithFilter, fetchTasks } = useTaskStore();
  const { allPremises, fetchPremises } = usePremisesStore();

  useEffect(() => {
    fetchTasks();
    if (!allPremises.length) fetchPremises();
  }, [fetchTasks, allPremises, fetchPremises]);

  const handleRegionChange = (region: string) => {
    setSelectedRegion(region);
    setSelectedPremises("");
  };

  const handlePremisesChange = (premises: string) => {
    setSelectedPremises(premises);
  };

  const handleModuleChange = (module: string) => {
    setSelectedModule(module);
  };

  const filteredTasks = useMemo(() => {
    let filtered = tasks;

    // Filter by region if selected and premises is not selected
    if (selectedRegion && !selectedPremises) {
      const premisesInRegion = allPremises
        .filter((p) => p.region === selectedRegion)
        .map((p) => p.id);
      filtered = filtered.filter((task) => !!task.premisesId && premisesInRegion.includes(task.premisesId));
    }

    // Filter by premises if selected
    if (selectedPremises) {
      filtered = filtered.filter(
        (task) => task.premisesId === selectedPremises
      );
    }

    // Filter by module if selected
    if (selectedModule) {
      filtered = filtered.filter((task) => task.module === selectedModule);
    }

    return filtered;
  }, [tasks, selectedRegion, selectedPremises, selectedModule, allPremises]);

  const displayedTasks = useMemo(() => {
    if (tasksWithFilter && tasksWithFilter.length > 0) {
      return tasksWithFilter;
    }
    if (filteredTasks.length > 0 || selectedPremises || selectedModule || selectedRegion) {
      return filteredTasks;
    }
    return tasks;
  }, [tasks, tasksWithFilter, filteredTasks, selectedPremises, selectedModule, selectedRegion]);

  const statusCounts = useMemo(() => {
    let total = 0;
    let pendingCount = 0;
    let completedCount = 0;
    let overdueCount = 0;

    displayedTasks.forEach((task) => {
      total++;
      const status = task.status?.toLowerCase();
      if (status === "completed") {
        completedCount++;
      } else if (status === "overdue") {
        overdueCount++;
      } else {
        pendingCount++;
      }
    });

    return {
      total,
      pendingCount,
      completedCount,
      overdueCount,
    };
  }, [displayedTasks]);

  return (
    <>
      <div className="px-2 py-4 mt-14 w-full h-full">
        <div className="flex justify-between items-center mb-4">
          <h1>Tasks</h1>
          <span className="flex gap-8">
            <Dropdown
              selectedValue={selectedRegion}
              onChange={handleRegionChange}
              type="region"
            />
            <Dropdown
              selectedValue={selectedPremises}
              onChange={handlePremisesChange}
              type="premises"
              selectedRegion={selectedRegion}
            />
            {canCreate(TASK__MODULE) && (
              <span className="flex items-center">
                <button
                  className="flex items-center justify-center font-semibold gap-3 bg-[#0fa9a6] text-white px-4 py-2 rounded-full"
                  onClick={() => setIsOpen(true)}
                >
                  <img src={images.addPremiessIcon} width={25} alt="" />
                  ADD TASK
                </button>
              </span>
            )}
          </span>
        </div>

        <div className="cardNum mt-4 mb-4">
          <div className="row">
            <div className="col-sm-3">
              <div className="card">
                <div className="cardIconCont">
                  <span className="font-medium text-md">Total Tasks</span>
                  <h3>{statusCounts.total}</h3>
                </div>
                <div className="cardIcon cardIconColor1">
                  <img className="iconTd" src={images.premisesIcon} alt="" />
                </div>
              </div>
            </div>
            <div className="col-sm-3">
              <div className="card">
                <div className="cardIconCont">
                  <span className="font-medium text-md">Pending Tasks</span>
                  <h3>{statusCounts.pendingCount}</h3>
                </div>
                <div className="cardIcon cardIconColor2">
                  <img className="iconTd" src={images.premisesIcon} alt="" />
                </div>
              </div>
            </div>
            <div className="col-sm-3">
              <div className="card">
                <div className="cardIconCont">
                  <span className="font-medium text-md">Completed Tasks</span>
                  <h3>{statusCounts.completedCount}</h3>
                </div>
                <div className="cardIcon cardIconColor3">
                  <img className="iconTd" src={images.premisesIcon} alt="" />
                </div>
              </div>
            </div>
            <div className="col-sm-3">
              <div className="card">
                <div className="cardIconCont">
                  <span className="font-medium text-md">Overdue Tasks</span>
                  <h3>{statusCounts.overdueCount}</h3>
                </div>
                <div className="cardIcon cardIconColor4">
                  <img className="iconTd" src={images.premisesIcon} alt="" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-content-between items-center mb-4 gap-4">
          <label className="text-lg font-medium text-gray-700 mb-1">
            Module
          </label>

          <select
            value={selectedModule}
            className="border-2 border-[#a5b7cf] rounded-md px-2 py-2 max-w-72 w-full focus:outline-none focus:ring-2 focus:ring-[#0fa9a6] focus:border-transparent"
            onChange={(e) => handleModuleChange(e.target.value)}
          >
            <option value="">All Modules</option>
            {TASK_MODULE_LIST_FIRE_SAFETY.map((module) => (
              <option key={module.value} value={module.value}>
                {module.label}
              </option>
            ))}
          </select>
        </div>

        <div className="mt-4">
          <TaskTable
            selectedRegion={selectedRegion}
            selectedPremises={selectedPremises}
            selectedModule={selectedModule}
          />
        </div>

        {isOpen && <AddTask showModal={isOpen} setShowModal={setIsOpen} />}
      </div>
    </>
  );
};

export default Tasks;
